import * as React from "react";
import apiClient from "@/api-client";
// The agent configuration form, reused unchanged for each of the caller's three services.
import { Service } from "@/new-pages/dashboard/agents/create/service";
import { Alert, Autocomplete, Box, Chip, Divider, Stack, Tab, Tabs, TextField, Typography } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";

import { logger } from "@/lib/default-logger";
import { toast } from "@/components/core/toaster";

const SERVICE_TABS = [
	{ value: "stt", label: "Transcription" },
	{ value: "llm", label: "Model" },
	{ value: "tts", label: "Voice" },
];

/**
 * Languages offered as suggestions rather than a fixed list — free text is allowed, because
 * a caller population is a local question and no list we ship will cover it.
 */
const LANGUAGE_SUGGESTIONS = [
	"English",
	"Malay",
	"Mandarin",
	"Cantonese",
	"Tamil",
	"Thai",
	"Indonesian",
	"Vietnamese",
	"Tagalog",
	"Hindi",
	"Arabic",
	"Japanese",
	"Korean",
];

/**
 * What a new persona starts with, so a library of a dozen callers does not mean configuring
 * the same three providers a dozen times — and so a generated caller arrives runnable rather
 * than needing to be opened and given a voice one at a time.
 */
export function PersonaDefaults({
	languages,
	onLanguagesChange,
}: {
	languages: string[];
	onLanguagesChange: (languages: string[]) => void;
}): React.JSX.Element {
	const { control, setValue, getValues } = useFormContext();

	const [tab, setTab] = React.useState("llm");
	const [plugins, setPlugins] = React.useState<Record<string, any[]>>({});
	const [fields, setFields] = React.useState<Record<string, any[]>>({});

	/** Which plugin each service's fields were last fetched for, so a repeat call is free. */
	const loadedFor = React.useRef<Record<string, string | undefined>>({});

	/**
	 * Fetches a plugin's configurable fields and seeds the form with them.
	 *
	 * Seeding differs by why we are here, and both behaviours are load-bearing. Arriving at a
	 * plugin for the first time — page load, or the parent's reset landing — must keep what
	 * was saved, or opening the page would quietly overwrite the operator's configuration
	 * with the plugin's defaults. Switching to a different plugin must not: carrying a
	 * Deepgram `model` value across to Soniox leaves a value that plugin has never heard of,
	 * which passes silently here and fails validation much later, when generated personas are
	 * accepted.
	 */
	const syncFields = async (service: string, pluginId: string) => {
		if (loadedFor.current[service] === pluginId) return;
		const previous = loadedFor.current[service];
		loadedFor.current[service] = pluginId;

		if (!pluginId) {
			setFields((prev) => ({ ...prev, [service]: [] }));
			return;
		}

		try {
			const response = await apiClient.get(`/plugin-services/${service}/${pluginId}`);
			if (loadedFor.current[service] !== pluginId) return; // a newer pick landed first
			setFields((prev) => ({ ...prev, [service]: response.data }));

			const keep = previous ? {} : (getValues()?.defaults?.[service]?.settings ?? {});
			setValue(
				`defaults.${service}.settings`,
				Object.fromEntries(
					response.data.map((field: any) => [
						field.name,
						keep[field.name] === undefined ? field.value : keep[field.name],
					])
				)
			);
		} catch (error) {
			logger.error(error);
			toast.error("Could not load that plugin's settings");
		}
	};

	// The plugin lists are independent of each other and of the form, so fetch them together.
	// Sequentially was the bug: each service only got its settings if the parent's reset had
	// already landed, so whichever ran last (voice) worked and the two before it did not.
	React.useEffect(() => {
		void Promise.all(
			SERVICE_TABS.map(async ({ value }) => {
				try {
					const response = await apiClient.get(`/plugin-services/${value}`);
					setPlugins((prev) => ({ ...prev, [value]: response.data }));
				} catch (error) {
					logger.error(error);
				}
			})
		);
	}, []);

	// Field loading is driven by the form value rather than by mount, so it happens whenever
	// the plugin becomes known — when the saved settings arrive, and when the operator picks
	// a different provider — instead of only if the settings happened to arrive first.
	const sttPlugin = useWatch({ control, name: "defaults.stt.pluginId" }) as string | undefined;
	const llmPlugin = useWatch({ control, name: "defaults.llm.pluginId" }) as string | undefined;
	const ttsPlugin = useWatch({ control, name: "defaults.tts.pluginId" }) as string | undefined;

	React.useEffect(() => {
		void syncFields("stt", sttPlugin ?? "");
		void syncFields("llm", llmPlugin ?? "");
		void syncFields("tts", ttsPlugin ?? "");
	}, [sttPlugin, llmPlugin, ttsPlugin]);

	return (
		<Stack spacing={3}>
			<Box>
				<Typography variant="h6" sx={{ mb: 0.5 }}>
					Languages
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					What generated callers speak unless the description says otherwise.
				</Typography>

				<Autocomplete
					multiple
					freeSolo
					options={LANGUAGE_SUGGESTIONS}
					value={languages}
					onChange={(_event, value) => onLanguagesChange(value as string[])}
					renderTags={(value, getTagProps) =>
						value.map((option, index) => {
							const { key, ...rest } = getTagProps({ index });
							return <Chip key={key} size="small" label={option} {...rest} />;
						})
					}
					renderInput={(params) => (
						<TextField
							{...params}
							placeholder="Add a language"
							helperText="Type anything — the list is only a shortcut."
						/>
					)}
				/>
			</Box>

			<Divider />

			<Box>
				<Typography variant="h6" sx={{ mb: 0.5 }}>
					Default providers
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Applied to every generated caller, and pre-filled when you create one by hand.
				</Typography>

				<Alert severity="info" sx={{ mb: 2 }}>
					Give callers a different voice from the agents they test, or a stereo recording becomes very hard to follow —
					and a different model family, or they share the agent&rsquo;s blind spots.
				</Alert>

				<Tabs value={tab} onChange={(_event, value: string) => setTab(value)} sx={{ mb: 2 }}>
					{SERVICE_TABS.map((item) => (
						<Tab key={item.value} value={item.value} label={item.label} sx={{ minHeight: "auto" }} />
					))}
				</Tabs>

				{/* Keyed by tab: settings fields are keyed by name, and `model` exists in all three
				    schemas, so without this the same input instance is reused across providers. */}
				<Service
					key={tab}
					control={control}
					service={`defaults.${tab}`}
					plugins={plugins[tab] ?? []}
					onPluginChange={(_service: string, pluginId: string) => void syncFields(tab, pluginId)}
					settings={fields[tab] ?? []}
				/>
			</Box>
		</Stack>
	);
}
